import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';

/**
 * イベント詳細 (申込状況) ページのデータ層フック。
 *
 * 忘年会申込サイト (Heteml CGI) が登録する mst_event / trn_event_* を
 * get_event_management_data で一括取得し、申込・参加者・入金・協賛・成績・賞品の授与の一覧と
 * サマリー集計を提供する。
 * 対象イベントはルートパラメータ由来の eventId で固定する (一覧ページで選択)。
 */

// イベントマスタ (mst_event) の1件
export type EventMaster = {
  id: number;
  event_code: string;
  event_name: string;
  event_type: string | null;
  event_date: string | null;
  venue: string | null;
  fee_regular: number | null;
  fee_prepaid: number | null;
  entry_start: string | null;
  entry_end: string | null;
  is_active: boolean;
  mail_subject: string | null;
  mail_body: string | null;
  entry_url: string | null;
  chat_webhook_url: string | null;
  // 配置の呼び名 (組/テーブル、打順/席 など。イベントごとに変更可)
  group_unit_label: string | null;
  position_unit_label: string | null;
  // 成績の形式 (mst_event_result_format.format_code)。null は成績を記録しないイベント
  result_format_code: string | null;
};

// 成績の形式 (mst_event_result_format)。fields の key が成績 details のキーと対応する
export type EventResultField = { key: string; label: string; type: 'number' | 'text' };

export type EventResultFormat = {
  format_code: string;
  format_name: string;
  score_label: string;
  // asc は小さいほど上位 (ゴルフの Net)、desc は大きいほど上位 (ボウリングのトータル)
  score_order: 'asc' | 'desc';
  team_score_label: string | null;
  fields: EventResultField[];
};

// 成績 (trn_event_result) の1件。個人は参加者の会社名・配置を併記、チームは team_no で配置のグループと対応
export type EventResultRow = {
  id: number;
  result_scope: '個人' | 'チーム';
  participant_id: number | null;
  team_no: string | null;
  team_name: string | null;
  rank_no: number | null;
  score: number | null;
  details: Record<string, string | number | null>;
  note: string | null;
  reception_no: string | null;
  company_name: string | null;
  last_name: string | null;
  first_name: string | null;
  is_staff: boolean | null;
  table_no: string | null;
  seat_no: string | null;
};

// 賞品の授与 (trn_event_award) の1件。順位は成績から、協賛元は協賛品の申込から解決済み。
// 当選者未定の賞品枠は participant_id と当選者側の項目 (受付NO・会社名・氏名・配置) が null
export type EventAwardRow = {
  id: number;
  award_type: '表彰' | '抽選';
  award_name: string;
  prize_name: string | null;
  sort_order: number | null;
  is_received: boolean;
  received_at: string | null;
  note: string | null;
  participant_id: number | null;
  reception_no: string | null;
  company_name: string | null;
  last_name: string | null;
  first_name: string | null;
  is_staff: boolean | null;
  table_no: string | null;
  rank_no: number | null;
  sponsorship_id: number | null;
  sponsorship_product_name: string | null;
  sponsor_reception_no: string | null;
  sponsor_company_name: string | null;
  // 賞品の画像の表示用 URL (ダウンロードトークン付き)。null は画像なし
  image_url: string | null;
};

// 賞品の授与の追加・更新の入力。更新は送った項目だけを変える (participant_id: null は当選者未定)
export type EventAwardInput = {
  award_type?: '表彰' | '抽選';
  award_name?: string | null;
  prize_name?: string | null;
  sponsorship_id?: number | null;
  participant_id?: number | null;
  sort_order?: number | null;
  is_received?: boolean;
  note?: string | null;
};

// 賞品の授与の並び (発表順、未設定は末尾。同順は登録順)。サーバーの ORDER BY と同じ
const sortAwards = (rows: EventAwardRow[]): EventAwardRow[] =>
  [...rows].sort((a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity) || a.id - b.id);

// 再取得せずに行を差し替えられる項目 (当選者・協賛品を変えると JOIN の表示名が変わるため再取得する)
const AWARD_LOCAL_KEYS: (keyof EventAwardInput)[] = ['is_received', 'sort_order'];

/** 賞品の画像として受け付ける元ファイルの上限 (サーバー側 EventWrite._AWARD_IMAGE_MAX_BYTES と同じ) */
export const AWARD_IMAGE_MAX_BYTES = 20 * 1024 * 1024;
// これより大きい写真は送る前にブラウザで縮小する (送信量を抑える。最終の縮小・EXIF 除去はサーバー側)
const AWARD_IMAGE_PRESHRINK_BYTES = 4 * 1024 * 1024;
const AWARD_IMAGE_PRESHRINK_WIDTH = 2400;

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error || new Error('ファイルを読み込めませんでした'));
    r.readAsDataURL(blob);
  });
}

/** 大きな写真をブラウザで縮小する (GIF はアニメーションを保つためそのまま)。失敗時は元ファイルを返す */
async function preshrinkAwardImage(file: File): Promise<Blob> {
  if (file.type === 'image/gif' || file.size <= AWARD_IMAGE_PRESHRINK_BYTES || typeof createImageBitmap !== 'function') return file;
  try {
    const bmp = await createImageBitmap(file); // 写真の向き (EXIF) は反映された状態で読まれる
    const scale = Math.min(1, AWARD_IMAGE_PRESHRINK_WIDTH / bmp.width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d')?.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.92));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

// 申込 (trn_event_entry) の1件。入金状況 (trn_event_payment) を LEFT JOIN で併記
export type EventEntryRow = {
  id: number;
  reception_no: string;
  attendance: string;
  entry_type: string | null;
  company_name: string;
  company_kana: string | null;
  department: string | null;
  tel: string | null;
  industry: string | null;
  industry_other: string | null;
  sponsorship: boolean | null;
  sponsorship_carry_method: string | null;
  receipt_name: string | null;
  hozenall_person: string | null;
  hozenall_branch: string | null;
  participants_count: number | null;
  absence_reason: string | null;
  mail_sent: boolean | null;
  entry_date: string | null;
  is_paid: boolean | null;
  expected_amount: number | null;
  paid_amount: number | null;
  // 配信元 (申込URLの listID → 配信予定 entry_token 突合で解決。トークンなし申込は null)
  invitee_id: number | null;
  invitee_company_name: string | null;
  invitee_person_name: string | null;
};

// 参加者 (trn_event_participant) の1件。申込の受付NO・会社名を併記
export type EventParticipantRow = {
  id: number;
  entry_id: number;
  reception_no: string;
  company_name: string;
  attendance: string;
  hozenall_person: string | null;
  hozenall_branch: string | null;
  // 申込の当社担当 (mst_account.user_id)。顧客の新規登録時の当社担当の初期値に使う
  hozenall_user_id: string | null;
  branch_no: string;
  last_name: string | null;
  first_name: string | null;
  last_kana: string | null;
  first_kana: string | null;
  position: string | null;
  // 表示用の役職 (申込入力 → 社員は mst_account → 社外は個人顧客 CRM の役職 の順に解決)
  position_display: string | null;
  email: string | null;
  customer_id: string | null;
  // 紐付いた個人顧客 (CRM) の氏名と勤務先。未紐付けは null
  customer_name: string | null;
  customer_employer_name: string | null;
  // 当社社員 (mst_account.user_id)。社外参加者は null
  staff_user_id: string | null;
  is_staff: boolean;
  // 配置 (trn_event_seat)。未配置は seat_id が null
  seat_id: number | null;
  table_no: string | null;
  seat_no: string | null;
  group_note: string | null;
};

// 参加者の編集・追加の入力 (customer_id は編集ではキーを送ったときだけ紐付けを更新する)
export type EventParticipantInput = {
  last_name: string | null;
  first_name: string | null;
  last_kana: string | null;
  first_kana: string | null;
  position: string | null;
  email: string | null;
  customer_id?: string | null;
};

// 配置の保存入力 (参加者単位。グループ番号と順番がともに空なら配置解除)
export type EventAssignmentInput = {
  participant_id: number;
  table_no: string | null;
  seat_no: string | null;
  group_note: string | null;
};

// 入金 (trn_event_payment) の1件。difference は生成列 (入金予定 - 入金額)
export type EventPaymentRow = {
  id: number;
  reception_no: string;
  company_name: string;
  participants_count: number | null;
  hozenall_person: string | null;
  hozenall_branch: string | null;
  expected_amount: number | null;
  paid_amount: number | null;
  paid_date: string | null;
  difference: number | null;
  is_paid: boolean | null;
  note: string | null;
};

// 協賛品 (trn_event_sponsorship) の1件
export type EventSponsorshipRow = {
  id: number;
  reception_no: string;
  company_name: string;
  hozenall_person: string | null;
  hozenall_branch: string | null;
  product_name: string | null;
  quantity: number | null;
  carry_method: string | null;
  scheduled_date: string | null;
  carried_date: string | null;
  storage_location: string | null;
  staff: string | null;
  is_received: boolean | null;
  received_date: string | null;
  thankyou_recipient: string | null;
  thankyou_sent: boolean | null;
};

// 配信予定 (trn_event_invitee) の1件。CRM の個人・法人名を JOIN で解決済み。
// 個人未特定の法人宛行は customer_id / person_name が空
export type EventInviteeRow = {
  id: number;
  source_row_no: number | null;
  customer_id: string | null;
  company_customer_id: string | null;
  company_name: string | null;
  company_email: string | null;
  person_name: string | null;
  person_title: string | null;
  business_email: string | null;
  our_staff_id: string | null;
  staff_name: string | null;
  request_first_mail: boolean | null;
  request_bulk_mail: boolean | null;
  self_invite: boolean | null;
  no_invite: boolean | null;
  coop_regular_member: boolean | null;
  send_to_name: string | null;
  send_to_email: string | null;
  plan_status: string | null;
  invited_at: string | null;
  note: string | null;
  entry_token: string | null;
};

export function useEventManagement(eventId: number) {
  const [event, setEvent] = useState<EventMaster | null>(null);
  const [entries, setEntries] = useState<EventEntryRow[]>([]);
  const [participants, setParticipants] = useState<EventParticipantRow[]>([]);
  const [payments, setPayments] = useState<EventPaymentRow[]>([]);
  const [sponsorships, setSponsorships] = useState<EventSponsorshipRow[]>([]);
  const [invitees, setInvitees] = useState<EventInviteeRow[]>([]);
  const [resultFormat, setResultFormat] = useState<EventResultFormat | null>(null);
  const [results, setResults] = useState<EventResultRow[]>([]);
  const [teamResults, setTeamResults] = useState<EventResultRow[]>([]);
  const [awards, setAwards] = useState<EventAwardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!Number.isFinite(eventId)) {
      setError('イベントの指定が不正です。');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(ep('get_event_management_data'), { event_id: eventId });
      if (res.data.status === 'success') {
        const d = res.data.data || {};
        setEvent(d.event || null);
        setEntries(d.entries || []);
        setParticipants(d.participants || []);
        setPayments(d.payments || []);
        setSponsorships(d.sponsorships || []);
        setInvitees(d.invitees || []);
        setResultFormat(d.result_format || null);
        setResults(d.results || []);
        setTeamResults(d.team_results || []);
        setAwards(d.awards || []);
      } else {
        setError('イベントデータの取得に失敗しました。');
      }
    } catch (e) {
      console.error('イベントデータの取得エラー:', e);
      setError('イベントデータの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const reload = fetchData;

  // 入金消込 (入金額の更新 or 入金済フラグの手動上書き)。
  // 成功時はサーバーの確定値 (difference は生成列) で該当行を差し替え、
  // 申込タブ側の入金状況列も reception_no で同期する。ページ全体の再取得はしない。
  const updatePayment = useCallback(
    async (
      paymentId: number,
      patch: { paid_amount?: number | null; is_paid?: boolean; note?: string | null; paid_date?: string | null },
    ): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('update_event_payment'), { payment_id: paymentId, ...patch });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '入金情報の更新に失敗しました。' };
        }
        const d = res.data.data as {
          id: number;
          paid_amount: number | null;
          paid_date: string | null;
          difference: number | null;
          is_paid: boolean;
          note: string | null;
        };
        const target = payments.find((p) => p.id === d.id);
        setPayments((prev) =>
          prev.map((p) =>
            p.id === d.id
              ? { ...p, paid_amount: d.paid_amount, paid_date: d.paid_date, difference: d.difference, is_paid: d.is_paid, note: d.note }
              : p,
          ),
        );
        if (target) {
          setEntries((prev) =>
            prev.map((e) =>
              e.reception_no === target.reception_no ? { ...e, paid_amount: d.paid_amount, is_paid: d.is_paid } : e,
            ),
          );
        }
        return { ok: true };
      } catch (e: any) {
        console.error('入金情報の更新エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '入金情報の更新に失敗しました。' };
      }
    },
    [payments],
  );

  // 協賛品の部分更新 (商品名・点数・日付・保管場所・受領・御礼状。全社員の運営入力用)。
  // 成功時はサーバー確定値で該当行の更新対象フィールドのみ差し替える。
  const updateSponsorship = useCallback(
    async (
      sponsorshipId: number,
      patch: Partial<
        Pick<
          EventSponsorshipRow,
          | 'product_name'
          | 'quantity'
          | 'scheduled_date'
          | 'carried_date'
          | 'storage_location'
          | 'staff'
          | 'received_date'
          | 'is_received'
          | 'thankyou_sent'
          | 'thankyou_recipient'
        >
      >,
    ): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('update_event_sponsorship'), { sponsorship_id: sponsorshipId, ...patch });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '協賛品の更新に失敗しました。' };
        }
        const d = res.data.data as Omit<EventSponsorshipRow, 'reception_no' | 'company_name' | 'hozenall_person' | 'hozenall_branch'>;
        setSponsorships((prev) => prev.map((r) => (r.id === d.id ? { ...r, ...d } : r)));
        return { ok: true };
      } catch (e: any) {
        console.error('協賛品の更新エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '協賛品の更新に失敗しました。' };
      }
    },
    [],
  );

  // 配信予定の部分更新 (案内希望フラグ・送付先・備考・plan_status)。
  // 成功時はサーバーの JOIN 解決済み確定行で該当行のみ差し替える。
  const updateInvitee = useCallback(
    async (
      inviteeId: number,
      patch: Partial<
        Pick<
          EventInviteeRow,
          | 'request_first_mail'
          | 'request_bulk_mail'
          | 'self_invite'
          | 'no_invite'
          | 'coop_regular_member'
          | 'send_to_name'
          | 'send_to_email'
          | 'note'
          | 'plan_status'
        >
      >,
    ): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('update_event_invitee'), { invitee_id: inviteeId, ...patch });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '配信予定の更新に失敗しました。' };
        }
        const d = res.data.data as EventInviteeRow;
        setInvitees((prev) => prev.map((r) => (r.id === d.id ? d : r)));
        return { ok: true };
      } catch (e: any) {
        console.error('配信予定の更新エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '配信予定の更新に失敗しました。' };
      }
    },
    [],
  );

  // 配信予定への CRM 顧客追加 (個人は勤務先法人も自動紐づけ)。追加行は先頭に挿入する
  const addInvitee = useCallback(
    async (customerId: string): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('add_event_invitee'), { event_id: eventId, customer_id: customerId });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '配信予定への追加に失敗しました。' };
        }
        const d = res.data.data as EventInviteeRow;
        setInvitees((prev) => [d, ...prev]);
        return { ok: true };
      } catch (e: any) {
        console.error('配信予定への追加エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '配信予定への追加に失敗しました。' };
      }
    },
    [eventId],
  );

  // 案内メール文面 (件名・本文) の保存。成功時はサーバー確定値でイベント情報を差し替える
  const updateEventMail = useCallback(
    async (patch: { mail_subject: string | null; mail_body: string | null }): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('update_event_mail'), { event_id: eventId, ...patch });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '案内メール文面の保存に失敗しました。' };
        }
        const d = res.data.data as { id: number; mail_subject: string | null; mail_body: string | null };
        setEvent((prev) => (prev ? { ...prev, mail_subject: d.mail_subject, mail_body: d.mail_body } : prev));
        return { ok: true };
      } catch (e: any) {
        console.error('案内メール文面の保存エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '案内メール文面の保存に失敗しました。' };
      }
    },
    [eventId],
  );

  // 案内メールの個別送信 (操作者本人名義)。成功時はサーバー確定行 (案内送付済) で差し替える
  const sendInviteeMail = useCallback(
    async (inviteeId: number, operatorEmail: string): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('send_event_invitee_mail'), {
          invitee_id: inviteeId,
          operator_email: operatorEmail,
        });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '案内メールの送信に失敗しました。' };
        }
        const d = res.data.data as EventInviteeRow;
        setInvitees((prev) => prev.map((r) => (r.id === d.id ? d : r)));
        return { ok: true };
      } catch (e: any) {
        console.error('案内メールの送信エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '案内メールの送信に失敗しました。' };
      }
    },
    [],
  );

  // 配信予定の削除依頼 (実行委員会 Chat へ通知するだけでレコードは変更しない)
  const requestDeleteInvitee = useCallback(
    async (inviteeId: number, operatorEmail: string): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('request_delete_event_invitee'), {
          invitee_id: inviteeId,
          operator_email: operatorEmail,
        });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '削除依頼の通知に失敗しました。' };
        }
        return { ok: true };
      } catch (e: any) {
        console.error('削除依頼の通知エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '削除依頼の通知に失敗しました。' };
      }
    },
    [],
  );

  // 配信予定からの除外 (行削除)
  const deleteInvitee = useCallback(async (inviteeId: number): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await axios.post(ep('delete_event_invitee'), { invitee_id: inviteeId });
      if (res.data.status !== 'success') {
        return { ok: false, error: res.data.error || '配信予定の削除に失敗しました。' };
      }
      setInvitees((prev) => prev.filter((r) => r.id !== inviteeId));
      return { ok: true };
    } catch (e: any) {
      console.error('配信予定の削除エラー:', e);
      return { ok: false, error: e?.response?.data?.error || '配信予定の削除に失敗しました。' };
    }
  }, []);

  // ヘッダーのサマリーカード用集計 (出席申込ベース)
  const summary = useMemo(() => {
    const attend = entries.filter((e) => e.attendance === '出席');
    const absent = entries.filter((e) => e.attendance === '欠席');
    const attendParticipants = attend.reduce((s, e) => s + (e.participants_count || 0), 0);
    const paidCount = payments.filter((p) => p.is_paid).length;
    const unpaidCount = payments.filter((p) => !p.is_paid).length;
    const expectedAmount = payments.reduce((s, p) => s + (p.expected_amount || 0), 0);
    const paidAmount = payments.reduce((s, p) => s + (p.paid_amount || 0), 0);
    const mailUnsent = attend.filter((e) => !e.mail_sent).length;
    return {
      attendCount: attend.length,
      absentCount: absent.length,
      attendParticipants,
      paidCount,
      unpaidCount,
      expectedAmount,
      paidAmount,
      sponsorCount: sponsorships.length,
      mailUnsent,
    };
  }, [entries, payments, sponsorships]);

  // 配置 (組合表 / 席次表 / 投順表) の一括保存。グループ番号と順番が空の行は配置解除。
  // 保存後はサーバーを正として再取得する (順番の振り直しが全行に及ぶため)
  const saveAssignments = useCallback(
    async (rows: EventAssignmentInput[]): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('save_event_assignments'), { event_id: eventId, rows });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '配置の保存に失敗しました。' };
        }
        await reload();
        return { ok: true };
      } catch (e: any) {
        console.error('配置の保存エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '配置の保存に失敗しました。' };
      }
    },
    [eventId, reload],
  );

  // 当社社員を参加者として追加する (社員配置用)。追加後は未配置に入るため再取得する
  const addStaffParticipants = useCallback(
    async (staffUserIds: string[]): Promise<{ ok: boolean; error?: string; added?: number; skipped?: number }> => {
      try {
        const res = await axios.post(ep('add_event_staff_participants'), {
          event_id: eventId,
          staff_user_ids: staffUserIds,
        });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '社員の追加に失敗しました。' };
        }
        const d = res.data.data as { added?: number; skipped?: number };
        await reload();
        return { ok: true, added: d?.added, skipped: d?.skipped };
      } catch (e: any) {
        console.error('社員の追加エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '社員の追加に失敗しました。' };
      }
    },
    [eventId, reload],
  );

  // 社員の参加者行を配置ごと外す
  const removeStaffParticipant = useCallback(
    async (participantId: number): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('remove_event_staff_participant'), { participant_id: participantId });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '社員の除外に失敗しました。' };
        }
        setParticipants((prev) => prev.filter((r) => r.id !== participantId));
        return { ok: true };
      } catch (e: any) {
        console.error('社員の除外エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '社員の除外に失敗しました。' };
      }
    },
    [],
  );

  // 参加者の氏名・フリガナ・役職・メールの修正と顧客の紐付け。紐付けた顧客の氏名を一覧へ出すため再取得する
  const updateParticipant = useCallback(
    async (participantId: number, input: EventParticipantInput, operatorEmail?: string): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('update_event_participant'), {
          participant_id: participantId,
          ...input,
          operator_email: operatorEmail,
        });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '参加者の更新に失敗しました。' };
        }
        await reload();
        return { ok: true };
      } catch (e: any) {
        console.error('参加者の更新エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '参加者の更新に失敗しました。' };
      }
    },
    [reload],
  );

  // お連れ様の追加 (個人顧客の紐付け必須)。申込の人数・入金予定額も変わるため再取得する
  const addParticipant = useCallback(
    async (
      entryId: number,
      input: EventParticipantInput,
      operatorEmail?: string,
    ): Promise<{ ok: boolean; error?: string; expectedAmount?: number | null }> => {
      try {
        const res = await axios.post(ep('add_event_participant'), { entry_id: entryId, ...input, operator_email: operatorEmail });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || 'お連れ様の追加に失敗しました。' };
        }
        await reload();
        return { ok: true, expectedAmount: res.data.data?.expected_amount ?? null };
      } catch (e: any) {
        console.error('お連れ様の追加エラー:', e);
        return { ok: false, error: e?.response?.data?.error || 'お連れ様の追加に失敗しました。' };
      }
    },
    [reload],
  );

  // お連れ様の取消 (配置ごと削除)。申込の人数・入金予定額も変わるため再取得する
  const cancelParticipant = useCallback(
    async (participantId: number, operatorEmail?: string): Promise<{ ok: boolean; error?: string; expectedAmount?: number | null }> => {
      try {
        const res = await axios.post(ep('cancel_event_participant'), { participant_id: participantId, operator_email: operatorEmail });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || 'お連れ様の取消に失敗しました。' };
        }
        await reload();
        return { ok: true, expectedAmount: res.data.data?.expected_amount ?? null };
      } catch (e: any) {
        console.error('お連れ様の取消エラー:', e);
        return { ok: false, error: e?.response?.data?.error || 'お連れ様の取消に失敗しました。' };
      }
    },
    [reload],
  );

  // 申込の取消 (欠席へ切替。参加者・配置を削除し、入金の記録があれば予定額 0 で入金行を残す)
  const cancelEntry = useCallback(
    async (
      entryId: number,
      operatorEmail?: string,
    ): Promise<{ ok: boolean; error?: string; paymentKept?: boolean; paidAmount?: number | null }> => {
      try {
        const res = await axios.post(ep('cancel_event_entry'), { entry_id: entryId, operator_email: operatorEmail });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '申込の取消に失敗しました。' };
        }
        await reload();
        return { ok: true, paymentKept: !!res.data.data?.payment_kept, paidAmount: res.data.data?.paid_amount ?? null };
      } catch (e: any) {
        console.error('申込の取消エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '申込の取消に失敗しました。' };
      }
    },
    [reload],
  );

  // 賞品の授与の追加 (awardId なし)・更新。受け取り・発表順だけの変更は該当行を差し替えて並べ直し、
  // それ以外 (当選者・協賛品・賞名など) は表示名を引き直すため再取得する
  const saveAward = useCallback(
    async (
      awardId: number | null,
      input: EventAwardInput,
      operatorEmail?: string,
    ): Promise<{ ok: boolean; error?: string; awardId?: number }> => {
      try {
        const res = await axios.post(ep('save_event_award'), {
          ...(awardId != null ? { award_id: awardId } : { event_id: eventId }),
          ...input,
          operator_email: operatorEmail,
        });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '賞品の授与の保存に失敗しました。' };
        }
        const keys = Object.keys(input) as (keyof EventAwardInput)[];
        if (awardId != null && keys.every((k) => AWARD_LOCAL_KEYS.includes(k))) {
          setAwards((prev) => sortAwards(prev.map((a) => (a.id === awardId ? { ...a, ...input } as EventAwardRow : a))));
        } else {
          await reload();
        }
        return { ok: true, awardId: res.data.data?.award_id ?? awardId ?? undefined };
      } catch (e: any) {
        console.error('賞品の授与の保存エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '賞品の授与の保存に失敗しました。' };
      }
    },
    [eventId, reload],
  );

  const deleteAward = useCallback(async (awardId: number, operatorEmail?: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await axios.post(ep('delete_event_award'), { award_id: awardId, operator_email: operatorEmail });
      if (res.data.status !== 'success') {
        return { ok: false, error: res.data.error || '賞品の授与の削除に失敗しました。' };
      }
      setAwards((prev) => prev.filter((a) => a.id !== awardId));
      return { ok: true };
    } catch (e: any) {
      console.error('賞品の授与の削除エラー:', e);
      return { ok: false, error: e?.response?.data?.error || '賞品の授与の削除に失敗しました。' };
    }
  }, []);

  // 協賛品から当選者未定の賞品枠を作る (点数から割当済みを引いた分)。作った行を出すため再取得する
  const createAwardSlots = useCallback(
    async (
      sponsorshipIds: number[],
      awardType: '表彰' | '抽選',
      awardName: string,
      operatorEmail?: string,
    ): Promise<{ ok: boolean; error?: string; created?: number }> => {
      try {
        const res = await axios.post(ep('create_event_award_slots'), {
          event_id: eventId,
          sponsorship_ids: sponsorshipIds,
          award_type: awardType,
          award_name: awardName,
          operator_email: operatorEmail,
        });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '賞品枠の作成に失敗しました。' };
        }
        await reload();
        return { ok: true, created: res.data.data?.created ?? 0 };
      } catch (e: any) {
        console.error('賞品枠の作成エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '賞品枠の作成に失敗しました。' };
      }
    },
    [eventId, reload],
  );

  // 発表順の並べ替え (ドラッグ)。awardIds はイベントの全行を新しい順に並べたもので、発表順を 1 から振り直す
  const reorderAwards = useCallback(
    async (awardIds: number[], operatorEmail?: string): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await axios.post(ep('reorder_event_awards'), {
          event_id: eventId,
          award_ids: awardIds,
          operator_email: operatorEmail,
        });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '発表順の並べ替えに失敗しました。' };
        }
        const position = new Map(awardIds.map((id, i) => [id, i + 1]));
        setAwards((prev) => sortAwards(prev.map((a) => ({ ...a, sort_order: position.get(a.id) ?? a.sort_order }))));
        return { ok: true };
      } catch (e: any) {
        console.error('発表順の並べ替えエラー:', e);
        return { ok: false, error: e?.response?.data?.error || '発表順の並べ替えに失敗しました。' };
      }
    },
    [eventId],
  );

  // 賞品の画像を付ける (付いていれば置き換える)。大きな写真は送る前に縮小する
  const uploadAwardImage = useCallback(
    async (awardId: number, file: File, operatorEmail?: string): Promise<{ ok: boolean; error?: string }> => {
      if (!file.type.startsWith('image/')) return { ok: false, error: '画像ファイル (JPEG / PNG / GIF / WebP) を選んでください。' };
      if (file.size > AWARD_IMAGE_MAX_BYTES) return { ok: false, error: '画像が大きすぎます (20MB まで)。' };
      try {
        const content = await blobToBase64(await preshrinkAwardImage(file));
        const res = await axios.post(ep('upload_event_award_image'), {
          award_id: awardId,
          content_base64: content,
          filename: file.name,
          operator_email: operatorEmail,
        });
        if (res.data.status !== 'success') {
          return { ok: false, error: res.data.error || '賞品の画像のアップロードに失敗しました。' };
        }
        const imageUrl: string | null = res.data.data?.image_url ?? null;
        setAwards((prev) => prev.map((a) => (a.id === awardId ? { ...a, image_url: imageUrl } : a)));
        return { ok: true };
      } catch (e: any) {
        console.error('賞品の画像のアップロードエラー:', e);
        return { ok: false, error: e?.response?.data?.error || '賞品の画像のアップロードに失敗しました。' };
      }
    },
    [],
  );

  const deleteAwardImage = useCallback(async (awardId: number, operatorEmail?: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await axios.post(ep('delete_event_award_image'), { award_id: awardId, operator_email: operatorEmail });
      if (res.data.status !== 'success') {
        return { ok: false, error: res.data.error || '賞品の画像を外せませんでした。' };
      }
      setAwards((prev) => prev.map((a) => (a.id === awardId ? { ...a, image_url: null } : a)));
      return { ok: true };
    } catch (e: any) {
      console.error('賞品の画像の削除エラー:', e);
      return { ok: false, error: e?.response?.data?.error || '賞品の画像を外せませんでした。' };
    }
  }, []);

  return {
    event,
    entries,
    participants,
    payments,
    sponsorships,
    invitees,
    resultFormat,
    results,
    teamResults,
    awards,
    loading,
    error,
    summary,
    reload,
    updatePayment,
    updateSponsorship,
    updateEventMail,
    updateInvitee,
    addInvitee,
    deleteInvitee,
    requestDeleteInvitee,
    sendInviteeMail,
    saveAssignments,
    addStaffParticipants,
    removeStaffParticipant,
    updateParticipant,
    addParticipant,
    cancelParticipant,
    cancelEntry,
    saveAward,
    deleteAward,
    createAwardSlots,
    reorderAwards,
    uploadAwardImage,
    deleteAwardImage,
  };
}

export type UseEventManagementResult = ReturnType<typeof useEventManagement>;
